import LoadError from "@/components/LoadError";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Minus, ArrowDown, ArrowUp, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { Button, Input, Spinner, Disclaimer, useToast } from "@/components/earnos/kit";
import { colors, money } from "@/lib/theme";

function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-line bg-surface-2 p-4">
      <p className="text-xl font-extrabold" style={{ color }}>{money(value || 0)}</p>
      <p className="text-sm text-ink-2 mt-0.5">{label}</p>
    </div>
  );
}

export default function Earnings() {
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [modal, setModal] = useState(null); // "income" | "expense"
  const [amount, setAmount] = useState("");
  const [customer, setCustomer] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("received");
  const [category, setCategory] = useState("expense");
  const [desc, setDesc] = useState("");
  const submitLock = useRef(false);
  const requestKey = useRef(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setLoadError("");
    try { setData(await apiFetch("/earnings")); }
    catch (e) {
      setLoadError(e.message || "Service unavailable. Please retry."); toast.show(e?.message || "Failed to load", "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setAmount(""); setCustomer(""); setSource(""); setStatus("received"); setCategory("expense"); setDesc(""); };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) { toast.show("Enter a valid amount", "error"); return; }
    if (submitLock.current) return;
    submitLock.current = true; setSaving(true);
    const today = new Date().toISOString().slice(0, 10);
    try {
      const payload = modal === "income" ? { amount: amt, date: today, customer, source, payment_status: status, notes: "" } : { amount: amt, date: today, category, description: desc };
      const signature = JSON.stringify({kind:modal, ...payload});
      if (requestKey.current?.signature !== signature) {
        const random = new Uint8Array(16); window.crypto.getRandomValues(random);
        requestKey.current = {signature, id:Array.from(random, x=>x.toString(16).padStart(2,"0")).join("")};
      }
      await apiFetch(`/earnings/${modal}`, { method:"POST", body:{...payload, request_id:requestKey.current.id} });
      requestKey.current = null;
      toast.show("Saved", "success");
      setModal(null); resetForm(); load();
    } catch (e) { toast.show(e?.message || "Failed", "error"); }
    finally { submitLock.current = false; setSaving(false); }
  };

  const delEntry = async (kind, entry_id) => {
    if (submitLock.current) return;
    submitLock.current = true;
    try { await apiFetch(`/earnings/${kind}/${entry_id}`, { method:"DELETE" }); await load(); }
    catch (e) { toast.show(e.message || "Could not delete entry", "error"); }
    finally { submitLock.current = false; }
  };

  if (loading) return <Spinner className="py-24" />;

  const goal = data?.goal_amount || 0;
  const progress = goal ? Math.min(1, (data?.received || 0) / goal) : 0;
  const hasTx = (data?.incomes || []).length + (data?.expenses_list || []).length > 0;

  if (loadError && !loading) return <LoadError title="Earnings" message={loadError} onRetry={load} />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} aria-label="Go back" data-testid="earnings-back" className="text-ink lg:hidden"><ChevronLeft size={26} /></button>
        <h1 className="text-2xl font-extrabold tracking-tight text-ink">Earnings</h1>
      </div>

      <p className="text-sm text-ink-2 mb-4">These entries are user-reported. They are separate from provider-verified payments.</p>
      <div className="rounded-2xl p-6 bg-gradient-to-br from-[#059669] to-[#0F766E] text-white">
        <p className="text-base font-semibold text-white/85">Estimated net income</p>
        <p className="text-4xl font-extrabold tracking-tight mt-1">{money(data?.net || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        {goal ? (
          <>
            <div className="h-2 bg-white/25 rounded-full mt-5 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${progress * 100}%` }} /></div>
            <p className="text-sm text-white/85 mt-2">{Math.round(progress * 100)}% toward your {money(goal)} goal</p>
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <Stat label="Revenue received" value={data?.received} color={colors.success} />
        <Stat label="Pending revenue" value={data?.pending} color={colors.warning} />
        <Stat label="Refunds" value={data?.refunds} color={colors.error} />
        <Stat label="Expenses & fees" value={data?.expenses} color={colors.onSurfaceSecondary} />
      </div>

      <div className="flex gap-3 mt-5">
        <Button title="Add Income" icon={Plus} onClick={() => setModal("income")} full testID="add-income" />
        <Button title="Add Expense" icon={Minus} variant="secondary" onClick={() => setModal("expense")} full testID="add-expense" />
      </div>

      <h3 className="text-xl font-bold text-ink mt-8">Transactions</h3>
      {!hasTx ? (
        <div className="rounded-xl border border-line bg-surface-2 p-6 mt-3 text-center">
          <p className="text-lg font-bold text-ink">No earnings recorded yet</p>
          <p className="text-base text-ink-2 mt-1">Your first goal is to create an offer and reach potential customers.</p>
          <Button title="View Today's Money Move" small variant="secondary" onClick={() => navigate("/moves")} className="mt-4" />
        </div>
      ) : (
        <div className="space-y-2 mt-3">
          {(data?.incomes || []).map((inc) => (
            <div key={inc.entry_id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: colors.success + "22" }}><ArrowDown size={16} className="text-ok" /></div>
              <div className="flex-1 min-w-0"><p className="text-base font-semibold text-ink truncate">{inc.customer || inc.source || "Income"}</p><p className="text-sm text-ink-2">{inc.payment_status} · {inc.date}</p></div>
              <span className="text-lg font-extrabold text-ok">+{money(inc.amount)}</span>
              <button onClick={() => delEntry("income", inc.entry_id)} data-testid={`tx-del-${inc.entry_id}`} className="text-ink-2 ml-1"><X size={16} /></button>
            </div>
          ))}
          {(data?.expenses_list || []).map((exp) => (
            <div key={exp.entry_id} className="flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: colors.error + "22" }}><ArrowUp size={16} className="text-danger" /></div>
              <div className="flex-1 min-w-0"><p className="text-base font-semibold text-ink truncate">{exp.description || exp.category || "Expense"}</p><p className="text-sm text-ink-2">{exp.category} · {exp.date}</p></div>
              <span className="text-lg font-extrabold text-danger">-{money(exp.amount)}</span>
              <button onClick={() => delEntry("expense", exp.entry_id)} data-testid={`tx-del-${exp.entry_id}`} className="text-ink-2 ml-1"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <Disclaimer text={data?.disclaimer || ""} />

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setModal(null)}>
          <div role="dialog" aria-modal="true" aria-labelledby="earnings-entry-title" className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-surface-2 border border-line p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 id="earnings-entry-title" className="text-xl font-bold text-ink">{modal === "income" ? "Add Income" : "Add Expense"}</h3>
              <button type="button" aria-label="Close transaction form" disabled={saving} onClick={() => setModal(null)} className="text-ink"><X size={24} /></button>
            </div>
            <Input label="Amount ($)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" testID="entry-amount" />
            {modal === "income" ? (
              <>
                <Input label="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Optional" />
                <Input label="Income source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. Video service" />
                <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2">Payment status</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["received", "pending", "refund"].map((s) => <button key={s} onClick={() => setStatus(s)} className={`h-9 px-4 rounded-full text-base font-semibold border ${status === s ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{s}</button>)}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-wide text-ink-2 mb-2">Category</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["expense", "platform_fee", "processing_fee"].map((c) => <button key={c} onClick={() => setCategory(c)} className={`h-9 px-4 rounded-full text-base font-semibold border ${category === c ? "bg-brand border-brand-light text-white" : "bg-surface-3 border-line text-ink-3"}`}>{c.replace("_", " ")}</button>)}
                </div>
                <Input label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Optional" />
              </>
            )}
            <Button title="Save" onClick={submit} disabled={!Number.isFinite(Number(amount)) || Number(amount) <= 0} loading={saving} full testID="entry-save" />
          </div>
        </div>
      )}
    </div>
  );
}
